import {getMessage} from './messages';

function initForm() {
  function setCode(code: string|number) {
    document.getElementById('registration-code')!.textContent = String(code);
    document.getElementById('registration-code2')!.textContent = String(code);
  }

  function getHiddenPriceEl(): HTMLInputElement {
    return document.getElementById('total-price-hidden') as HTMLInputElement;
  }

  function setPrice(price: string|number) {
    for (const totalPriceValue of Array.from(document.querySelectorAll('.total-price-value'))) {
      totalPriceValue.textContent = `$${price}`;
    }
    getHiddenPriceEl().value = `$${price}`;
  }

  function setEmailSent(address: string) {
    document.getElementById('email-sent-details')!.style.display = '';
    document.getElementById('sent-email-address')!.textContent = address;
  }

  /**
   * Converts a kebab-case string to Title Case with spaces.
   * Example: 'child-full-name' -> 'Child Full Name'
   */
  function formatKebabToTitleCase(input: string): string {
    if (!input) return '';

    return input
      .split('-')
      .map((word) => {
        // Handle potential empty strings from multiple hyphens
        if (word.length === 0) return '';
        
        return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
      })
      .join(' ');
  }

  function clearErrors(section: HTMLElement) {
    const errorList = section.querySelector('.validation-errors') as HTMLUListElement;
    if (errorList) {
      errorList.innerHTML = '';
    }
    const missing = section.querySelectorAll('.missing-required');
    for (const el of missing) {
      el.classList.remove('missing-required');
    }
  }

  function addError(section: HTMLElement, inputId: string | null, message: string) {
    const errorList = section.querySelector('.validation-errors') as HTMLUListElement;
    if (errorList) {
      const li = document.createElement('li');
      li.textContent = message;
      errorList.appendChild(li);
    }
    if (inputId) {
      const el = document.getElementById(inputId);
      if (el && el.parentElement) {
        el.parentElement.classList.add('missing-required');
      }
    }
  }

  function getCurrentSection(): HTMLElement {
    return document.querySelector('.active-form-section') as HTMLElement
  }

  function validateCurrentSection(): boolean {
    const activeSection = getCurrentSection();
    switch (activeSection.id) {
      case 'form-section-1':
        return validateOrder();
      case 'form-section-2':
        return validatePersonalDetails();
      case 'form-section-3':
        return validateFinalSection();
      default:
        throw new Error('Unexpected active section');
    }
  }

  function validateOrder(): boolean {
    const section = document.getElementById('form-section-1')!;
    clearErrors(section);
    let isValid = true;

    const weekCheckboxes = document.querySelectorAll<HTMLInputElement>('.weeks-checkboxes input[name^="week"]');
    const selectedWeeks = Array.from(weekCheckboxes).filter(cb => cb.checked);
    if (selectedWeeks.length === 0) {
      addError(section, null, getMessage('required.oneweek'));
      isValid = false;
      const weeksContainer = document.querySelector('.weeks-checkboxes');
      if (weeksContainer) {
        weeksContainer.classList.add('missing-required');
      }
    }

    return isValid;
  }

  function validatePersonalDetails(): boolean {
    const section = document.getElementById('form-section-2')!;
    clearErrors(section);
    let isValid = true;

    const textFields = [
      { id: 'child-full-name', msg: getMessage('required.childFullName') },
      { id: 'child-dob', msg: getMessage('required.childDob') },
      { id: 'child-age', msg: getMessage('required.childAge') },
      { id: 'child-gender', msg: getMessage('required.childGender') },
      { id: 'parent-name', msg: getMessage('required.parentName') },
      { id: 'parent-relationship', msg: getMessage('required.parentRelationship') },
      { id: 'parent-phone', msg: getMessage('required.parentPhone') },
      { id: 'parent-email', msg: getMessage('required.parentEmail') },
      { id: 'parent-address', msg: getMessage('required.parentAddress') },
      { id: 'emergency-name', msg: getMessage('required.emergencyName') },
      { id: 'emergency-relationship', msg: getMessage('required.emergencyRelationship') },
      { id: 'emergency-phone', msg: getMessage('required.emergencyPhone') },
    ];

    // Courtesy of Stack Overflow
    const validateEmail = (email: string) => {
      return email.match(
        /^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
      );
    };

    for (const field of textFields) {
      const el = document.getElementById(field.id) as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;
      if (!el.value.trim() ||
          (el.id.includes('email') && !validateEmail(el.value.trim()))) {
        addError(section, field.id, field.msg);
        isValid = false;
      }
    }

    const langEn = document.getElementById('lang-en') as HTMLInputElement;
    const langFr = document.getElementById('lang-fr') as HTMLInputElement;
    const langOther = document.getElementById('lang-other') as HTMLInputElement;

    if (!langEn.checked && !langFr.checked && !langOther.value.trim()) {
      addError(section, null, getMessage('required.language'));
      isValid = false;
      const fieldset = langEn.closest('fieldset');
      if (fieldset) {
        fieldset.classList.add('missing-required');
      }
    }

    const allergiesYes = document.getElementById('allergies-yes') as HTMLInputElement;
    const allergiesNo = document.getElementById('allergies-no') as HTMLInputElement;
    const allergiesSpecify = document.getElementById('allergies-specify') as HTMLInputElement;
    if (!allergiesYes.checked && !allergiesNo.checked) {
      addError(section, null, getMessage('required.allergyInfo'));
      isValid = false;
      if (allergiesYes.parentElement?.parentElement) {
        allergiesYes.parentElement.parentElement.classList.add('missing-required');
      }
    } else if (allergiesYes.checked && !allergiesSpecify.value.trim()) {
      addError(section, 'allergies-specify', getMessage('required.allergySpecify'));
      isValid = false;
    }

    const medsYes = document.getElementById('meds-yes') as HTMLInputElement;
    const medsNo = document.getElementById('meds-no') as HTMLInputElement;
    const medsSpecify = document.getElementById('meds-specify') as HTMLInputElement;
    if (!medsYes.checked && !medsNo.checked) {
      addError(section, null, getMessage('required.medicationInfo'));
      isValid = false;
      if (medsYes.parentElement?.parentElement) {
        medsYes.parentElement.parentElement.classList.add('missing-required');
      }
    } else if (medsYes.checked && !medsSpecify.value.trim()) {
      addError(section, 'meds-specify', getMessage('required.medicationSpecify'));
      isValid = false;
    }

    return isValid;
  }

  function validateFinalSection(): boolean {
    const section = document.getElementById('form-section-3')!;
    clearErrors(section);
    let isValid = true;

    const termCheckboxes = Array.from(section.querySelectorAll('.terms-checkboxes input')) as HTMLInputElement[];
    let termsValid = true;
    for (const cb of termCheckboxes) {
      if (!cb.checked) {
        termsValid = false;
        if (cb.parentElement) {
          cb.parentElement.classList.add('missing-required');
        }
      }
    }

    if (!termsValid) {
      addError(section, null, getMessage('required.terms'));
      isValid = false;
    }

    return isValid;
  }

  function getNavHeaderPadding(): number {
    return document.body.offsetWidth < 600 ? 55 : 0;
  }

  function initContinueButton(buttonId: string) {
    const button = document.getElementById(buttonId)!;
    button.addEventListener('click', (e: MouseEvent) => {     
      e.preventDefault();
      e.stopPropagation();
      if (!validateCurrentSection()) return;

      const currentSection = button.closest('.form-section') as HTMLElement;
      const nextSection = currentSection.nextElementSibling as HTMLElement;
      const nextSectonBottom = nextSection.offsetTop + nextSection.offsetHeight;
      const nextSectionDistanceFromScroll = nextSection.offsetTop - window.scrollY;
      const isNextSectionFullVisible =
          window.scrollY + window.innerHeight > nextSectonBottom;

      currentSection.classList.add('complete-form-section');
      currentSection.classList.remove('active-form-section');
      nextSection.classList.add('active-form-section');

      const nextSameSpot = nextSection.offsetTop - nextSectionDistanceFromScroll;
      if (isNextSectionFullVisible && nextSameSpot > 200) {
        window.scrollTo(0, nextSection.offsetTop - nextSectionDistanceFromScroll);
      } else {
        window.scrollTo(0, currentSection.offsetTop - 10 - getNavHeaderPadding());
      }
    });
  }

  /**
   * When a user has already pressed Continue and this section has collapsed,
   * clicking on the collapsed section will re-open it.
   */
  function initPreviousSectionClick(id: string) {
    const section = document.getElementById(id) as HTMLElement;
    section.addEventListener('click', () => {
      if (!section.classList.contains('complete-form-section')) {
        return;
      }
      const current = document.querySelector('.active-form-section');
      if (current) { // This should always be true.
        current.classList.remove('active-form-section');
        section.classList.remove('complete-form-section');
      }
      section.classList.add('active-form-section');
      section.classList.remove('complete-form-section');
    });
  }

  function initWeeksCheckboxes() {
    const selectAll = document.getElementById('select-all-weeks') as HTMLInputElement;
    const weekCheckboxes = document.querySelectorAll<HTMLInputElement>('.weeks-checkboxes input[name^="week"]');
    const standardPrices = document.querySelectorAll<HTMLElement>('.weeks-checkboxes .price');
    const discountPrices = document.querySelectorAll<HTMLElement>('.weeks-checkboxes .price-discount');

    function updatePricingDisplay(isDiscounted: boolean) {
      for (const el of standardPrices) {
        el.style.display = isDiscounted ? 'none' : '';
      }
      for (const el of discountPrices) {
        el.style.display = isDiscounted ? '' : 'none';
      }
    }

    function areAllWeeksSelected(): boolean {
      return Array.from(weekCheckboxes).every(cb => cb.checked);
    };

    selectAll.addEventListener('change', () => {
      const isChecked = selectAll.checked;
      for (const cb of weekCheckboxes) {
        cb.checked = isChecked;
      }
      updatePricingDisplay(isChecked);
      updatePrice();
    });

    for (const weekCheckbox of weekCheckboxes) {
      weekCheckbox.addEventListener('change', () => {
        const allSelected = areAllWeeksSelected();
        selectAll.checked = allSelected;
        updatePricingDisplay(allSelected);
        updatePrice();
      });
    }
  }

  // Make it so that you can only select one or the other.
  function initEveningCheckboxes() {
    const cb1 = document.getElementById('select-extended-evening-1') as HTMLInputElement;
    const cb2 = document.getElementById('select-extended-evening-2') as HTMLInputElement;
    cb1.addEventListener('change', () => {
      if (cb1.checked) {
        cb2.checked = false;
      }
      updatePrice();
    });
    cb2.addEventListener('change', () => {
      if (cb2.checked) {
        cb1.checked = false;
      }
      updatePrice();
    });
  }

  function updatePrice() {
    const weekCheckboxes = document.querySelectorAll<HTMLInputElement>('.weeks-checkboxes input[name^="week"]');
    const morningCare = document.getElementById('select-extended-morning') as HTMLInputElement;
    const eveningCare1 = document.getElementById('select-extended-evening-1') as HTMLInputElement;
    const eveningCare2 = document.getElementById('select-extended-evening-2') as HTMLInputElement;
    const tshirtSize = document.getElementById('tshirt-size') as HTMLSelectElement;
    const priceItemized = document.getElementById('price-itemized')!;

    let total = 0;
    const details: string[] = [];

    const selectedWeeks: HTMLInputElement[] = [];
    for (const cb of weekCheckboxes) {
      if (cb.checked) {
        selectedWeeks.push(cb);
      }
    }

    if (selectedWeeks.length > 0) {
      const weeksPrice = selectedWeeks.length === 4 ? 1000 : selectedWeeks.map(el => Number(el.dataset.price)).reduce((ac, n) => ac + n, 0);;
      total += weeksPrice;
      details.push(`${getMessage('price.camp')(selectedWeeks.length)}: $${weeksPrice}`);
    }

    if (morningCare.checked) {
      total += 20;
      details.push(`${getMessage('price.morningCare')}: $20`);
    }

    if (eveningCare1.checked) {
      total += 20;
      details.push(`${getMessage('price.eveningCare')}: $20`);
    }

    if (eveningCare2.checked) {
      total += 40;
      details.push(`${getMessage('price.eveningCare')}: $40`);
    }

    if (tshirtSize.value) {
      total += 20;
      details.push(`${getMessage('price.tshirt')}: $20`);
    }

    priceItemized.innerHTML = '';
    for (const detail of details) {
      const li = document.createElement('li');
      li.textContent = detail;
      priceItemized.appendChild(li);
    }

    setPrice(total);
  }

  function initYesNoDetails() {
    const containers = Array.from(document.querySelectorAll('.yes-no-details'));
    for (const container of containers) {
      const yesRadio = container.querySelector('input[value="yes"]') as HTMLInputElement;
      for (const radio of Array.from(container.querySelectorAll('input[type="radio"]'))) {
        radio.addEventListener('change', () => {
          const detailsContainer = container.querySelector('.details-container') as HTMLElement;
          detailsContainer.style.display = yesRadio.checked ? '' : 'none';

        });
      }
    }
  }

  function jsonifyForm(formEl: HTMLElement): string {
    var formData: Record<string, string> = {
      'Web Lang': document.documentElement.lang,
    };
    var inputs = Array.from(formEl.querySelectorAll('input, textarea') as NodeListOf<HTMLInputElement>);
    for (const el of inputs) {
      if (!el.name) continue;
      let value = el.value;
      if (el.type === 'checkbox') {
        const currentValue = formData[formatKebabToTitleCase(el.name)];
        if (!currentValue || currentValue === '❌') {
          value = el.checked ? el.value : '❌';
        } else if (el.value) {
          value = `${currentValue}, ${el.value}`;
        }
      } else if (el.type === 'radio' && !el.checked) {
        continue;
      }
      formData[formatKebabToTitleCase(el.name)] = value;
    }
    return JSON.stringify(formData);
  }

  const form = document.getElementById('camp-form') as HTMLFormElement;

  function showSuccess(): HTMLElement {
    const submitSuccess = document.getElementById('submit-success') as HTMLElement;
    submitSuccess.style.display = '';
    document.getElementById('register-form-container')!.style.display = 'none';
    return submitSuccess;
  }

  async function submitForm() {
    const formSubmitErrorMessage = document.getElementById('form-submit-error')!;
    formSubmitErrorMessage.style.display = 'none';

    const button = document.getElementById('submit-button') as HTMLButtonElement;
    button.disabled = true;

    const originalLabel = button.textContent;
    button.textContent = getMessage('button.processing') + '...';

    try {
      const response = await fetch('/.netlify/functions/camp-register', {
        method: 'POST',
        body: jsonifyForm(form),
      });
      if (!response.ok) {
        throw 'Error response code ' + response.status;
      }
      const jsonResponse = await response.json();
      const code = jsonResponse['code'];
      if (!code) {
        console.error(jsonResponse);
        throw new Error('Missing code from response');
      }
      const emailSent = jsonResponse['emailSent'] as string|null;
      if (emailSent) {
        setEmailSent(emailSent);
      }
      const isCampFull = jsonResponse['isCampFull'] as boolean;
      if (isCampFull) {
        document.getElementById('camp-open')!.style.display = 'none';
        document.getElementById('camp-full')!.style.display = '';
      }

      setCode(code);
      const submitSuccess = showSuccess();
      window.scrollTo(0, submitSuccess.offsetTop - 10 - getNavHeaderPadding());

      const url = new URL(location.href);
      url.searchParams.set('code', code);
      url.searchParams.set('price', getHiddenPriceEl().value.replace('$', ''));
      if (emailSent) {
        url.searchParams.set('email', emailSent);
      }
      history.replaceState(undefined, '', url.toString());
    } catch (error: unknown) {
      console.error(error);
      formSubmitErrorMessage.style.display = '';
      button.textContent = originalLabel;
      button.disabled = false;
    }
  }

  initContinueButton('order-continue-button');
  initContinueButton('personal-continue-button');

  initPreviousSectionClick('form-section-1');
  initPreviousSectionClick('form-section-2');

  initWeeksCheckboxes();
  initYesNoDetails();
  initEveningCheckboxes();

  // Listen for other things that affect price.
  document.getElementById('select-extended-morning')!.addEventListener('change', updatePrice);
  document.getElementById('tshirt-size')!.addEventListener('change', updatePrice);

  updatePrice();

  // If a field has a validation error, we want to clear it the moment they
  // interact with it.
  form.addEventListener('change', (e) => {
    const target = e.target as HTMLElement;
    if (target.closest('.missing-required')) {
      validateCurrentSection();
    }
  });

  form.addEventListener('keyup', (e) => {
    const target = e.target as HTMLElement;
    if ((target.tagName === 'INPUT' || target.tagName === 'TEXTAREA') && target.closest('.missing-required')) {
      validateCurrentSection();
    }
  });

  form.addEventListener('submit', (e: SubmitEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (!document.getElementById('form-section-3')!.classList.contains('active-form-section')) {
      // If the last section isn't visible yet, don't even try.
      return;
    }

    const allGood = validateFinalSection() && validatePersonalDetails && validatePersonalDetails();
    if (!allGood) {
      return;
    }
    submitForm();
  });

  // Hide the member only regisration.
  const isGeneralRegistration = new Date() >= new Date(2026, 5, 1);
  if (isGeneralRegistration) {
    document.getElementById('only-for-members')!.style.display = 'none';
    document.getElementById('term-member-only-container')!.remove();
  }

  // Check the URL and see if we should show the confirmation page.
  const url = new URL(location.href);
  const urlCode = url.searchParams.get('code');
  const urlPrice = url.searchParams.get('price');
  if (urlCode && urlPrice) {
    setCode(urlCode);
    setPrice(urlPrice);
    const email = url.searchParams.get('email');
    if (email) {
      setEmailSent(email);
    }
    showSuccess();
  }

  document.querySelector('main')!.style.display = 'block';
  document.querySelector('footer')!.style.display = 'block';
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initForm);
} else {
  initForm();
}